import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, input, Input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { filter, Observable, switchMap } from 'rxjs';
import { Sixty, Thousand, TwentyFour } from '../../shared/utils/constants/global.constants';
import { OpenStatementsService } from '../open-statements.service';
import { StatementDisplayComponent } from '../statement-display/statement-display.component';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { CsAgentAuthService } from '@spartacus/asm/root';
import { faFileExcel } from '@fortawesome/free-regular-svg-icons';
import { B2bUserService } from '../../shared/services/b2b-user.service';
import { StatementsDateMap, StatementsDateTable } from '../model/statment.model';
import { SalesOrg } from '../../shared/config/app.config';

const statementCssClass = 'col-md-10 col-sm-9';
const accountOverviewCssClass = 'col-md-12 col-sm-12';
const dateFormat = 'yyyy-MM-dd';

@Component({
  selector: 'app-open-statements',
  templateUrl: './open-statements.component.html',
  standalone: false
})
export class OpenStatementsComponent implements OnInit, OnDestroy {
  private b2bUserService = inject(B2bUserService);
  private stService = inject(OpenStatementsService);
  private cdr = inject(ChangeDetectorRef);
  private datePipe = inject(DatePipe);
  private modalService = inject(NgbModal);
  private destroyRef = inject(DestroyRef);
  private csAgentAuthService = inject(CsAgentAuthService);

  widgetView = input(false);
  salesOrg = signal<SalesOrg>(null);

  statements = output<boolean>();
  @Input() hideAside: boolean;
  showSection = false;
  recordLength = 10;
  statementWidth: string = statementCssClass;
  statementsData: StatementsDateTable;
  restrictToTen = input<boolean>(false);
  showPortal = false;
  details: any;
  accountId: string;
  isCustomerEmulated = false;
  loadingElementMap = new Map<StatementsDateMap, boolean>();
  private $b2bUnit: Observable<any>;

  protected readonly faFileExcel = faFileExcel;

  constructor() {
    this.$b2bUnit = toObservable(this.b2bUserService.b2bUser);
  }

  ngOnInit(): void {
    if (!!this.hideAside) {
      this.statementWidth = accountOverviewCssClass;
      this.showSection = this.hideAside;
    } else {
      this.statementWidth = statementCssClass;
      this.showSection = false;
    }

    this.csAgentAuthService.isCustomerEmulated()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isCustomerEmulated => this.isCustomerEmulated = isCustomerEmulated);

    this.$b2bUnit
      .pipe(
        filter(Boolean),
        switchMap(response => {
          this.accountId = response.uid;
          this.salesOrg.set(response?.salesOrg);

          return this.stService.getStatements({
            accountId: this.accountId,
            fields: 'DEFAULT',
          });
        }),
        filter(Boolean),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(res => {
        if (this.restrictToTen()) {
          this.recordLength = 10;
        } else {
          this.recordLength = res.dateMap.length;
        }

        this.statementsData = JSON.parse(JSON.stringify(res));
        this.statements.emit(!!this.statementsData?.dateMap?.length);
        this.statementsData?.dateMap.slice(0, this.recordLength).forEach((date) => {
          this.loadingElementMap.set(date, false);
        });
        this.cdr.detectChanges();
      });

    this.stService.reloadComponent
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res) => {
        this.showPortal = !res;
        this.modalService.dismissAll();
        this.cdr.detectChanges();
      });
    this.cdr.detectChanges();
  }

  openNewModalPopup(endDate, startDate) {
    this.details = { fromDte: startDate, toDte: endDate, print: false };
    const modalRef = this.modalService.open(StatementDisplayComponent, {
      size: 'lg',
      scrollable: false,
    });

    modalRef.componentInstance.toShow = true;
    modalRef.componentInstance.statement = this.details;
    modalRef.componentInstance.accountNumber = signal(this.accountId);
    modalRef.componentInstance.isCustomerEmulated = signal(this.isCustomerEmulated);
    modalRef.componentInstance.salesOrg = signal(this.salesOrg());
    this.stService.loadStatements.next(this.details);
  }

  viewCurrent() {
    const currentDate = new Date();
    const myDate = this.datePipe.transform(currentDate, dateFormat);
    const fromDateTimeStamp = new Date(
      new Date(this.statementsData?.dateMap[0].value).getTime() +
      TwentyFour * Sixty * Sixty * Thousand
    );
    const fromDate = this.datePipe.transform(fromDateTimeStamp, dateFormat);
    this.openNewModalPopup(myDate, fromDate);
  }

  getViewAllDate(): string {
    return this.datePipe.transform(new Date(), dateFormat);
  }

  ngOnDestroy() {
    this.modalService.dismissAll();
  }

  protected readonly dateFormat = dateFormat;
}
